import '../../styles/App.css';

import * as Sentry from '@sentry/react';
import React from 'react';

import { Button, Flex, Typography } from '@alpine-iq/ui-library';
import { userUtils, utils } from '../../helpers';
import ScrollView from '../ScrollView';
import Layout from '../ux/Layout';
import ErrorSVG from '../ux/svgs/Error';

export interface ErrorBoundaryProps {
	children: any;
	fallBack?: (errorInfo: ErrorInfo) => any | undefined;

	sendAlert?: boolean;
	fullPage?: boolean;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
	fallBack: any;
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.fallBack = props.fallBack;
	}

	render() {
		if (location.hostname === 'localhost') {
			return (
				<SimpleErrorBoundary
					fallBack={(errorinfo) =>
						this.fallBack || (
							<DefaultError
								fullPage={this.props.fullPage}
								errorInfo={errorinfo}
							/>
						)
					}
				>
					{this.props.children}
				</SimpleErrorBoundary>
			);
		}
		return (
			<Sentry.ErrorBoundary
				fallback={(errorinfo) =>
					this.fallBack || (
						<DefaultError
							fullPage={this.props.fullPage}
							errorInfo={errorinfo}
						/>
					)
				}
			>
				{this.props.children}
			</Sentry.ErrorBoundary>
		);
	}
}

type ErrorInfo = {
	componentStack: string;
	eventId: string;
	error: Error;
};

function DefaultError(props: { errorInfo: ErrorInfo; fullPage?: boolean }) {
	const [open, setOpen] = React.useState(utils.isLocal());

	return (
		<>
			<Layout style={{ height: '100%' }}>
				<Flex
					center
					padding="24px"
					style={{ height: '100%' }}
				>
					<Flex
						column
						center
						gap="32px"
					>
						<ErrorSVG />
						<Typography variant="h1">Something went wrong</Typography>

						<Flex gap="16px">
							<Button
								iconSide="right"
								onClick={() => setOpen(!open)}
								icon={open ? 'CHEVRON_UP' : 'CHEVRON_DOWN'}
							>
								{open ? 'Hide Details' : 'See Details'}
							</Button>
							<Button
								icon="REFRESH"
								variant="primary"
								onClick={() => document.location.reload()}
							>
								Refresh Page
							</Button>
						</Flex>

						{open && (
							<div style={{ maxWidth: 650 }}>
								<ScrollView height={200}>
									<Flex
										column
										css={{ color: 'var(--danger)' }}
									>
										<div>
											<b>Error:</b> {props.errorInfo.error.name}
										</div>
										<div>
											<b>Message:</b> {props.errorInfo.error.message}
										</div>
										{userUtils.isAdmin() && (
											<>
												<div>
													<b>Stack:</b> {props.errorInfo.error.stack}
												</div>
												<div>
													<b>Component Stack:</b> {props.errorInfo.componentStack}
												</div>
												<div>
													<b>Event ID:</b> {props.errorInfo.eventId}
												</div>
											</>
										)}
									</Flex>
								</ScrollView>
							</div>
						)}
					</Flex>
				</Flex>
			</Layout>
		</>
	);
}

export class SimpleErrorBoundary extends React.Component<ErrorBoundaryProps> {
	fallBack: any;
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.fallBack = props.fallBack;
	}

	state = { hasError: false, errorInfo: { error: new Error(), componentStack: '', eventId: '' } };

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, errorInfo: { error, componentStack: '', eventId: '' } };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		this.setState({ hasError: true, errorInfo: { error, componentStack: errorInfo.componentStack, eventId: '' } });
	}

	render() {
		if (this.state.hasError) {
			return this.fallBack(this.state.errorInfo);
		}
		return this.props.children;
	}
}
